import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { departmentApi } from '../../api/department.api';
import { userApi } from '../../api/user.api';
import { PageHeader, Btn, PasswordField } from '../../components/ui/index';
import { useUI } from '../../store/UIContext';
import { phoneError, normalizeIranianMobile, phoneInputChange } from '../../utils/validators';

const LABEL = { fontSize: 12, fontWeight: 600, color: '#334155' };

export default function AddDepartmentPage() {
  const navigate = useNavigate();
  const { addToast } = useUI();
  const [mode, setMode] = useState('new');
  const [employees, setEmployees] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState('');
  // Backend (addDepartmentWithNewUser) requires description/first_name/
  // last_name/image_data too - previously missing, plus the whole "new"
  // payload was sent as FormData while the view does json.loads(request.
  // body), and "exist" mode sent {employee_id,name} while the view reads
  // manager_name (a username, not an id)+description - both modes always
  // failed regardless of what was filled in.
  const [form, setForm] = useState({ name:'', display_name:'', description:'', username:'', first_name:'', last_name:'', password:'', password2:'', phone:'' });
  const [loading, setLoading] = useState(false);
  const inp = { padding:'10px 14px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none', fontFamily:'inherit', width:'100%', boxSizing:'border-box' };

  useEffect(() => {
    // getEmployeeList returns {employee_list:[usernames...]} - plain
    // strings, not {id,username} objects.
    userApi.getEmployeeList()
      .then(r => setEmployees(Array.isArray(r.data?.employee_list) ? r.data.employee_list : []))
      .catch(() => {});
  }, []);

  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const submit = async () => {
    if (mode === 'new' && (!form.phone.trim() || phoneError(form.phone))) {
      addToast('شماره موبایل معتبر الزامی است', 'error');
      return;
    }
    if (mode === 'new' && form.password !== form.password2) {
      addToast('تکرار رمز عبور با رمز عبور یکسان نیست', 'error');
      return;
    }
    setLoading(true);
    try {
      if (mode==='exist') {
        await departmentApi.addDepartmentWithExistUser({ manager_name: selectedEmp, name: form.name, display_name: form.display_name.trim(), description: form.description });
      } else {
        await departmentApi.addDepartmentWithNewUser({
          name: form.name,
          display_name: form.display_name.trim(),
          description: form.description,
          username: form.username,
          first_name: form.first_name,
          last_name: form.last_name,
          password: form.password,
          phone_number: normalizeIranianMobile(form.phone),
          image_data: '',
        });
      }
      addToast('دپارتمان افزوده شد', 'success');
      navigate('/companies');
    } catch { addToast('خطا در افزودن دپارتمان', 'error'); }
    setLoading(false);
  };

  return (
    <div className="fade-in">
      <PageHeader title="افزودن دپارتمان" />
      <div style={{maxWidth:520,background:'#fff',borderRadius:16,border:'1px solid rgba(35,58,122,0.1)',padding:32,boxShadow:'0 4px 16px rgba(35,58,122,0.08)'}}>
        <div style={{display:'flex',gap:8,marginBottom:24}}>
          {['new','exist'].map(m=><button key={m} onClick={()=>setMode(m)} style={{flex:1,padding:'9px',borderRadius:8,border:`1.5px solid ${mode===m?'#29AAE1':'#e2e8f0'}`,background:mode===m?'#EDF3FC':'#fff',color:mode===m?'#233A7A':'#64748b',fontWeight:700,fontSize:13,cursor:'pointer',fontFamily:'inherit'}}>{m==='new'?'کارمند جدید':'کارمند موجود'}</button>)}
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          <div><label style={{fontSize:12,fontWeight:600,color:'#334155',display:'block',marginBottom:5}}>نام دپارتمان *</label><input value={form.name} onChange={set('name')} style={inp}/></div>
          <div><label style={{fontSize:12,fontWeight:600,color:'#334155',display:'block',marginBottom:5}}>نام نمایشی</label><input value={form.display_name} onChange={set('display_name')} placeholder="نامی که به مسئولان نمایش داده می‌شود (اختیاری)" style={inp}/></div>
          <div><label style={{fontSize:12,fontWeight:600,color:'#334155',display:'block',marginBottom:5}}>توضیحات</label><input value={form.description} onChange={set('description')} style={inp}/></div>
          {mode==='exist'?(
            <div><label style={{fontSize:12,fontWeight:600,color:'#334155',display:'block',marginBottom:5}}>انتخاب کارمند (به‌عنوان مدیر دپارتمان)</label>
              <select value={selectedEmp} onChange={e=>setSelectedEmp(e.target.value)} style={{...inp,cursor:'pointer'}}>
                <option value="">-- انتخاب --</option>
                {employees.map(username=><option key={username} value={username}>{username}</option>)}
              </select>
            </div>
          ):(
            <>
              <div><label style={{fontSize:12,fontWeight:600,color:'#334155',display:'block',marginBottom:5}}>نام کاربری</label><input value={form.username} onChange={set('username')} style={inp}/></div>
              <div><label style={{fontSize:12,fontWeight:600,color:'#334155',display:'block',marginBottom:5}}>نام</label><input value={form.first_name} onChange={set('first_name')} style={inp}/></div>
              <div><label style={{fontSize:12,fontWeight:600,color:'#334155',display:'block',marginBottom:5}}>نام خانوادگی</label><input value={form.last_name} onChange={set('last_name')} style={inp}/></div>
              <PasswordField label="رمز عبور" labelStyle={LABEL} value={form.password} onChange={set('password')} style={{padding:'10px 14px 10px 42px'}}/>
              <PasswordField label="تکرار رمز عبور" labelStyle={LABEL} value={form.password2} onChange={set('password2')} error={form.password2 && form.password !== form.password2 ? 'تکرار رمز عبور با رمز عبور یکسان نیست' : ''}/>
              <div><label style={{fontSize:12,fontWeight:600,color:'#334155',display:'block',marginBottom:5}}>شماره موبایل *</label><input type="tel" inputMode="numeric" maxLength={11} value={form.phone} onChange={phoneInputChange(set('phone'))} placeholder="09xxxxxxxxx" dir="ltr" style={inp}/></div>
            </>
          )}
          <div style={{display:'flex',gap:10,marginTop:8}}>
            <Btn variant="ghost" onClick={()=>navigate(-1)}>انصراف</Btn>
            <Btn onClick={submit} loading={loading}>ذخیره</Btn>
          </div>
        </div>
      </div>
    </div>
  );
}